import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { upperFirst } from 'lodash';
import { catchError, Observable, shareReplay, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExpectedKpiListService {
  private readonly http = inject(HttpClient);
  private readonly platformFeatureKpisRequests = new Map<string, Observable<TExpectedKpi[]>>();

  public getPlatformFeatureKpisGoal(platformFeature: EPlatformFeature): Observable<TExpectedKpi[]> {
    const cacheKey = `goal:${platformFeature}`;
    const cachedRequest = this.platformFeatureKpisRequests.get(cacheKey);

    if (cachedRequest) {
      return cachedRequest;
    }

    const params = new HttpParams().set('platform_feature', upperFirst(platformFeature));

    const request$ = this.http.get<TExpectedKpi[]>(`platform-feature-kpis/kpi-goals`, { params }).pipe(
      catchError((error: unknown) => {
        this.platformFeatureKpisRequests.delete(cacheKey);

        return throwError(() => error);
      }),
      shareReplay(1),
    );

    this.platformFeatureKpisRequests.set(cacheKey, request$);

    return request$;
  }

  public getPlatformFeatureKpisPerformance(platformFeature: EPlatformFeature): Observable<TExpectedKpi[]> {
    const cacheKey = `performance:${platformFeature}`;
    const cachedRequest = this.platformFeatureKpisRequests.get(cacheKey);

    if (cachedRequest) {
      return cachedRequest;
    }

    const params = new HttpParams().set('platform_feature', upperFirst(platformFeature));

    const request$ = this.http.get<TExpectedKpi[]>(`platform-feature-kpis/performance`, { params }).pipe(
      catchError((error: unknown) => {
        this.platformFeatureKpisRequests.delete(cacheKey);

        return throwError(() => error);
      }),
      shareReplay(1),
    );

    this.platformFeatureKpisRequests.set(cacheKey, request$);

    return request$;
  }

  public getPlatformFeatureKpisPerformanceExport(
    campaignExportId: string,
    platformFeature: EPlatformFeature,
  ): Observable<TExpectedKpi[]> {
    const cacheKey = `export:${campaignExportId}:${platformFeature}`;
    const cachedRequest = this.platformFeatureKpisRequests.get(cacheKey);

    if (cachedRequest) {
      return cachedRequest;
    }

    const params = new HttpParams().set('platform_feature', upperFirst(platformFeature));

    const request$ = this.http
      .get<TExpectedKpi[]>(`platform-feature-kpis/performance/export/${campaignExportId}`, { params })
      .pipe(
        catchError((error: unknown) => {
          this.platformFeatureKpisRequests.delete(cacheKey);

          return throwError(() => error);
        }),
        shareReplay(1),
      );

    this.platformFeatureKpisRequests.set(cacheKey, request$);

    return request$;
  }

  public getPlatformFeaturesKpisPerformance(platformFeatures: EPlatformFeature[]): Observable<TExpectedKpi[]> {
    const params = new HttpParams().set(
      'platform_feature__in',
      platformFeatures.map((platformFeature) => upperFirst(platformFeature)).join(','),
    );

    return this.http.get<TExpectedKpi[]>(`platform-feature-kpis/performance`, { params });
  }

  public getPlatformFeaturesKpisExport(
    campaignExportId: string,
    platformFeatures: EPlatformFeature[],
  ): Observable<TExpectedKpi[]> {
    const params = new HttpParams().set(
      'platform_feature__in',
      platformFeatures.map((platformFeature) => upperFirst(platformFeature)).join(','),
    );

    return this.http.get<TExpectedKpi[]>(`platform-feature-kpis/export/${campaignExportId}`, { params });
  }
}
